import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import GridContainer from '@navi/web-ui/lib/layouts/Grid/GridContainer/GridContainer';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import { Card, Typography } from '@navi/web-ui/lib/primitives';
import { formatAmount } from 'src/utils/commonUtils';
import cx from 'classnames';
import styles from './index.module.scss';
import CashCollectedAmountIcon from 'src/assets/icons/CashCollectedAmountIcon';
import CallsAttemptedPhoneIcon from 'src/assets/icons/CallsAttemptedPhoneIcon';
import AmeyoCallsAttemptedPhoneIcon from 'src/assets/icons/AmeyoCallsAttemptedPhoneIcon';
import TotalPaidCasesIcon from 'src/assets/icons/TotalPaidCasesIcon';
import TotalUnpaidCasesIcon from 'src/assets/icons/TotalUnpaidCasesIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import CumulativeCashCollected from 'src/components/leaderboard/CumulativeCashCollected';
import PerformanceChart from 'src/components/leaderboard/PerformanceChart';
import { useEffect, useState } from 'react';
import StarLevelFiveIcon from 'src/assets/icons/StarLevelFiveIcon';
import StarLevelFourIcon from 'src/assets/icons/StarLevelFourIcon';
import StarLevelOneIcon from 'src/assets/icons/StarLevelOneIcon';
import StarLevelSevenIcon from 'src/assets/icons/StarLevelSevenIcon';
import StarLevelSixIcon from 'src/assets/icons/StarLevelSixIcon';
import StarLevelThreeIcon from 'src/assets/icons/StarLevelThreeIcon';
import StarLevelTwoIcon from 'src/assets/icons/StarLevelTwoIcon';
import ToggleTabs from 'src/components/toggleTabs/ToggleTabs';
import { ITabsOption } from 'src/components/interfaces';
import CashEarnedChartHC from '@cp/src/components/leaderboard/CashEarnedChart/CashEarnedChartHC';
import ProfileStatisticsChart from 'src/components/leaderboard/ProfileStatisticsChart';
import RangeMeter from 'src/components/leaderboard/RangeMeter';
import { dateFormat, getFormattedDateMonth, getServerTime, monthNames } from 'src/utils/DateHelper';
import { getCallBridgeData, getPerformance, getPromiseCount } from '../auth/AuthActions';
import { PERFORMANCE_CHART_TYPE } from 'src/components/leaderboard/leaderboardConstant';
import { FeedbackTypesCodeMap } from '../Cases/constants/CasesConstants';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../service/clickStream.constant';
import { LevelIcon } from 'src/assets/icons/LevelIcon';
import { levelColorsList } from '@cp/src/components/AgentprogressMeter/constant';
import AgentProgressMeter from '@cp/src/components/AgentprogressMeter';
import ActivityTracker from './ActivityTracker/ActivityTracker';
import TelePerformanceDashboard from '../TelePerformanceDashboard/TelePerformanceDashboard';
import CaseRecommendation from '../CaseRecommendation';

const LEVEL_ICONS = [
  <StarLevelOneIcon key={1} />,
  <StarLevelTwoIcon key={2} />,
  <StarLevelThreeIcon key={3} />,
  <StarLevelFourIcon key={4} />,
  <StarLevelFiveIcon key={5} />,
  <StarLevelSixIcon key={6} />,
  <StarLevelSevenIcon key={7} />
];

const LAST_SEVEN_DAYS = 7;
const LAST_THIRTY_DAYS = 30;

const tabOptions: ITabsOption[] = [
  {
    label: 'Last 7 Days',
    value: true
  },
  {
    label: 'Last 30 Days',
    value: false
  }
];
const legendwrapperStyle = { display: 'flex', left: '25px', top: '-15px' };
const leaderboardPointerStyle = { transform: 'scale(0.6)' };

const DashBoard = () => {
  const dispatch = useDispatch();

  const leaderboard = useSelector((store: RootState) => store?.leaderboard);
  const name = useSelector((store: RootState) => store?.common?.userData?.name);
  const agentReferenceId = useSelector((store: RootState) => store?.common?.userData?.referenceId);
  const monthlyPerformanceData = useSelector(
    (store: RootState) => store?.leaderboard?.monthlyPerformance
  );
  const weeklyPerformanceData = useSelector(
    (store: RootState) => store?.leaderboard?.weeklyPerformance
  );

  useEffect(() => {
    dispatch(getPromiseCount());
    dispatch(getCallBridgeData({ includeCallBridgeCount: true, includeAmeyoCallCount: true }));
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Dashboard_PageLand, {});
  }, []);

  const todaysDate = new Date(getServerTime());
  const lastMonthDate = new Date(
    new Date().setDate(
      todaysDate.getDate() -
        (leaderboard?.noOfDaysCashCollected ? leaderboard.noOfDaysCashCollected - 1 : 0)
    )
  );
  const callsData = leaderboard?.percentCompletedRecords;
  const totalCalls = callsData?.reduce(
    (cases, curr) => {
      return {
        totalPaidCases: cases.totalPaidCases + curr.paid,
        totalUnPaidCases: cases.totalUnPaidCases + curr.unpaid
      };
    },
    { totalPaidCases: 0, totalUnPaidCases: 0 }
  );
  const isSevenDaysCompleted = (leaderboard?.noOfDaysCashCollected || 0) >= LAST_SEVEN_DAYS;

  const cumulativeCashCollectedWidth = window?.innerWidth * 0.42;
  const [isCashCollectedWeekly, setIsCashCollectedWeekly] = useState(false);
  const [isPerformanceWeekly, setIsPerformaceWeekly] = useState(false);

  const isAgentRangeMeterPresent =
    leaderboard &&
    leaderboard.levelConstructList &&
    leaderboard.agentLevelRanking &&
    leaderboard.cashCollectedInLast30Days &&
    leaderboard.maxLevel;

  const cashCollectedData =
    isCashCollectedWeekly && leaderboard?.agentLevelRanking
      ? weeklyPerformanceData
      : monthlyPerformanceData;
  const performanceData = isPerformanceWeekly ? weeklyPerformanceData : monthlyPerformanceData;

  useEffect(() => {
    if (agentReferenceId) {
      dispatch(getPerformance(agentReferenceId, PERFORMANCE_CHART_TYPE.LAST_30_DAYS));
    }
  }, [agentReferenceId]);

  const handleCashCollected = (value: string | boolean) => {
    if (value !== isCashCollectedWeekly && agentReferenceId) {
      const chartType = value
        ? PERFORMANCE_CHART_TYPE.LAST_7_DAYS
        : PERFORMANCE_CHART_TYPE.LAST_30_DAYS;
      dispatch(getPerformance(agentReferenceId, chartType));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Dashboard_QuickFilterCashCollected, {
        tabSelected: chartType
      });
      setIsCashCollectedWeekly(!isCashCollectedWeekly);
    }
  };

  const handlePerformance = (value: string | boolean) => {
    if (value !== isPerformanceWeekly && agentReferenceId) {
      const chartType = value
        ? PERFORMANCE_CHART_TYPE.LAST_7_DAYS
        : PERFORMANCE_CHART_TYPE.LAST_30_DAYS;
      dispatch(getPerformance(agentReferenceId, chartType));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Dashboard_QuickFilterPerformanceLevel, {
        tabSelected: chartType
      });
      setIsPerformaceWeekly(!isPerformanceWeekly);
    }
  };

  const RENDERED_ON_DASHBOARD = 'dashboard';

  const isdrrTrackerEnabled = useSelector(
    (state: RootState) => state?.common?.featureFlags
  )?.drrTrackerEnabled;

  const isAgentAdherenceEnabled = useSelector(
    (state: RootState) => state.common.featureFlags?.agentAdherenceEnabled
  );
  const casePrioritisationEnabled = useSelector(
    (state: RootState) => state.common.featureFlags?.casePrioritisationEnabled
  );

  return (
    <>
      {casePrioritisationEnabled && <CaseRecommendation />}
      {isdrrTrackerEnabled && (
        <>
          <TelePerformanceDashboard />
          <div className="w-[97%] h-[1px] m-4 bg-[var(--greyscale-light)]" />
        </>
      )}
      <GridContainer style={{ paddingBottom: '20px', overflowY: 'auto', overflowX: 'hidden' }}>
        {!isdrrTrackerEnabled && (
          <GridColumn className={styles.agentName}>
            <Typography variant="h2" className={styles.nameText}>
              Hi, {name || ''}
            </Typography>
          </GridColumn>
        )}
        {isAgentAdherenceEnabled && <ActivityTracker />}
        <GridContainer className={styles.agentInsightsContainer}>
          <div className={styles.mainContainercardWrapper}>
            <div className={styles.topCardWrapperParent}>
              <Card className={styles.cardWrapper}>
                <div className={styles.cardWrapperChild}>
                  <Typography as="div" variant="p4" className={styles.subtext}>
                    Total Cash Collected
                  </Typography>
                  <Typography as="div" variant="h2" className={styles.amount}>
                    {leaderboard?.todaysCashCollected
                      ? formatAmount(leaderboard.todaysCashCollected)
                      : '--'}
                  </Typography>
                  <div className={styles.iconContainer}>
                    <CashCollectedAmountIcon opacity="0.2" />
                  </div>
                </div>
              </Card>
            </div>
            <div className={cx(styles.topCardWrapperParent)}>
              <Card className={styles.cardWrapper}>
                <div className={styles.cardWrapperChild}>
                  <Typography as="div" variant="p4" className={styles.subtext}>
                    Slash calls attempted
                  </Typography>
                  <Typography as="div" variant="h2" className={styles.amount}>
                    {leaderboard?.callbridgeData?.successfulCallBridgeCount || '--'}
                  </Typography>
                  <div className={styles.iconContainer}>
                    <CallsAttemptedPhoneIcon />
                  </div>
                </div>
              </Card>
            </div>
            <div className={cx(styles.topCardWrapperParent)}>
              <Card className={styles.cardWrapper}>
                <div className={styles.cardWrapperChild}>
                  <Typography as="div" variant="p4" className={styles.subtext}>
                    Ameyo calls attempted
                  </Typography>
                  <Typography as="div" variant="h2" className={styles.amount}>
                    {leaderboard?.callbridgeData?.successfulAmeyoAttemptedCount || '--'}
                  </Typography>
                  <div className={styles.iconContainer}>
                    <AmeyoCallsAttemptedPhoneIcon />
                  </div>
                </div>
              </Card>
            </div>
            <div className={cx(styles.topCardWrapperParent)}>
              <Card className={styles.cardWrapper}>
                <div className={styles.cardWrapperChild}>
                  <Typography as="div" variant="p4" className={styles.subtext}>
                    Total Paid Cases
                  </Typography>
                  <Typography as="div" variant="h2" className={styles.amount}>
                    {totalCalls?.totalPaidCases}
                  </Typography>
                  <div className={styles.iconContainer}>
                    <TotalPaidCasesIcon />
                  </div>
                </div>
              </Card>
            </div>
            <div className={cx(styles.topCardWrapperParent, styles.paddingRight_8)}>
              <Card className={styles.cardWrapper}>
                <div className={styles.cardWrapperChild}>
                  <Typography as="div" variant="p4" className={styles.subtext}>
                    Total Unpaid Cases
                  </Typography>
                  <Typography as="div" variant="h2" className={styles.amount}>
                    {totalCalls?.totalUnPaidCases}
                  </Typography>
                  <div className={styles.iconContainer}>
                    <TotalUnpaidCasesIcon />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </GridContainer>
        <GridContainer className={styles.agentStatisticsContainer}>
          <GridRow className={styles.tableChartWrapperStyles}>
            <GridColumn md={6} className={styles.paddingRight_12}>
              <GridContainer className={styles.agentFeedbackContainer}>
                <GridRow className={styles.tableHeader}>
                  <GridColumn md={8}>
                    <Typography variant="h4">Feedback</Typography>
                  </GridColumn>
                  <GridColumn md={2}>
                    <Typography variant="h4">Paid (Nos.)</Typography>
                  </GridColumn>
                  <GridColumn md={2}>
                    <Typography variant="h4">Unpaid (Nos.)</Typography>
                  </GridColumn>
                </GridRow>
                <GridContainer style={{ padding: '0px' }}>
                  {callsData?.map(row => (
                    <GridRow key={row.interactionStatus} className={styles.tableContent}>
                      <GridColumn md={8} className={styles.contentText}>
                        <Typography variant="p3">
                          {' '}
                          {FeedbackTypesCodeMap[row.interactionStatus]?.label ||
                            row.interactionStatus}
                        </Typography>
                      </GridColumn>
                      <GridColumn md={2} className={styles.contentText}>
                        <Typography variant="p3">{row.paid}</Typography>
                      </GridColumn>
                      <GridColumn md={2} className={styles.contentText}>
                        <Typography variant="p3">{row.unpaid}</Typography>
                      </GridColumn>
                    </GridRow>
                  ))}
                  <GridRow className={styles.tableContent}>
                    <GridColumn md={8} className={cx(styles.contentText, styles.textbold)}>
                      <Typography variant="h4">TOTAL</Typography>
                    </GridColumn>
                    <GridColumn md={2} className={cx(styles.contentText, styles.textbold)}>
                      <Typography variant="h4">{totalCalls?.totalPaidCases || 0}</Typography>
                    </GridColumn>
                    <GridColumn md={2} className={cx(styles.contentText, styles.textbold)}>
                      <Typography variant="h4">{totalCalls?.totalUnPaidCases || 0}</Typography>
                    </GridColumn>
                  </GridRow>
                </GridContainer>
              </GridContainer>
            </GridColumn>
            <GridColumn md={6} className={styles.paddingLeft_12}>
              <GridContainer className={cx(styles.cardShadow, styles.promiseStatistics)}>
                <GridRow className={styles.promiseStatisticsTextContainer}>
                  <Typography variant="h4">Promise Statistics</Typography>
                </GridRow>
                <GridRow mt={2}>
                  <ProfileStatisticsChart
                    pieChartContainerClass={styles.cashEarnedChartHCpieChartClass}
                    {...leaderboard}
                  />
                </GridRow>
              </GridContainer>
            </GridColumn>
          </GridRow>
        </GridContainer>
        <GridContainer className={styles.overAllPerformanceContainer}>
          <GridRow>
            <GridColumn className={styles.overAllPerformaceText}>Overall Performance</GridColumn>
          </GridRow>
          <GridRow>
            {isSevenDaysCompleted ? (
              <>
                <GridColumn md={6} className={cx(styles.paddingRight_12, styles.paddingBottom_16)}>
                  <GridContainer className={styles.cardShadow}>
                    <GridRow className={styles.cashCollectedHeading}>
                      <GridColumn className={styles.paddingLeft_0}>
                        <Typography variant="p3" className={styles.headingText}>
                          {`Total Cash Collected ${
                            leaderboard?.agentLevelRanking
                              ? ''
                              : `(Past ${leaderboard?.noOfDaysCashCollected || '--'} days)`
                          }`}
                        </Typography>
                      </GridColumn>
                      {leaderboard?.agentLevelRanking ? (
                        <GridColumn className={styles.cashCollectedToggleButton}>
                          <ToggleTabs
                            selected={isCashCollectedWeekly}
                            onTabClick={value => handleCashCollected(value)}
                            tabOptions={tabOptions}
                            size="h6"
                          />
                        </GridColumn>
                      ) : null}
                    </GridRow>
                    <CumulativeCashCollected
                      data={cashCollectedData}
                      height={206}
                      width={cumulativeCashCollectedWidth}
                      legendWrapperStyle={legendwrapperStyle}
                      graphWrapperContainerClass={styles.graphWrapperContainerClass}
                      isWeekData={isCashCollectedWeekly && leaderboard?.agentLevelRanking}
                    />
                  </GridContainer>
                </GridColumn>
                <GridColumn md={6} className={cx(styles.paddingLeft_12, styles.paddingBottom_16)}>
                  <GridContainer className={cx(styles.cardShadow, styles.performanceLevel)}>
                    <GridRow className={styles.performanceLevelHeading}>
                      <GridColumn className={styles.paddingLeft_0}>
                        <Typography variant="p3" className={styles.headingText}>
                          Performance Level
                        </Typography>
                      </GridColumn>
                      {leaderboard?.agentLevelRanking ? (
                        <>
                          <GridColumn className={styles.performanceLevelToggleButton}>
                            <ToggleTabs
                              selected={isPerformanceWeekly}
                              onTabClick={value => handlePerformance(value)}
                              tabOptions={tabOptions}
                              size="h6"
                            />
                          </GridColumn>
                          <PerformanceChart
                            data={performanceData}
                            height={206}
                            width={cumulativeCashCollectedWidth}
                            legendWrapperStyle={legendwrapperStyle}
                            graphWrapperContainerClass={styles.graphWrapperContainerClass}
                            isWeekData={isPerformanceWeekly}
                          />
                        </>
                      ) : (
                        <div className={styles.peformanceDataNotPresent}>
                          <span>
                            Your level will be reflected on{' '}
                            {leaderboard?.dateWhenRankingVisible
                              ? getFormattedDateMonth(new Date(leaderboard.dateWhenRankingVisible))
                              : '--'}
                          </span>
                          <span> (after completing 30 days)</span>
                        </div>
                      )}
                    </GridRow>
                  </GridContainer>
                </GridColumn>
              </>
            ) : null}

            <GridColumn md={6} className={cx(styles.paddingRight_12, styles.paddingBottom_16)}>
              <GridContainer className={cx(styles.cardShadow, styles.paddingTop_16, 'px-0')}>
                <CashEarnedChartHC
                  titleContainerClass={styles.cashEarnedChartHCtitleClass}
                  pieChartContainerClass={styles.cashEarnedChartHCpieChartClass}
                  animation={false}
                  showDropdown
                  {...leaderboard}
                  renderedOn={RENDERED_ON_DASHBOARD}
                />
              </GridContainer>
            </GridColumn>
            <GridColumn md={6} className={cx(styles.paddingLeft_12, styles.paddingBottom_16)}>
              <GridContainer
                className={cx(
                  styles.cardShadow,
                  styles.agentPerformanceLevelCard,
                  isAgentRangeMeterPresent ? styles.agentPerformanceBackground : ''
                )}
              >
                <GridRow className={styles.agentPerformanceTopRow}>
                  <GridColumn md={8}>
                    <GridRow>
                      <Typography variant="p3">
                        {`Last ${
                          leaderboard?.noOfDaysCashCollected || '--'
                        } Days Cash Collected (${dateFormat(
                          lastMonthDate,
                          'DD MMM'
                        )} - ${dateFormat(todaysDate, 'DD MMM')})`}
                      </Typography>
                    </GridRow>
                    <GridRow style={{ marginTop: '10px' }}>
                      <Typography variant="h1">
                        {leaderboard?.cashCollectedInLast30Days
                          ? formatAmount(leaderboard.cashCollectedInLast30Days)
                          : '--'}
                      </Typography>
                    </GridRow>
                  </GridColumn>
                  <GridColumn md={4}>
                    <GridRow>
                      <Typography variant="p3">Your Performance Level</Typography>
                    </GridRow>
                    <GridRow style={{ marginTop: '10px' }}>
                      {leaderboard?.agentLevelRanking ? (
                        <Typography variant="p5" className={styles.agentCurrLevelIcon}>
                          <LevelIcon
                            level={leaderboard?.agentLevelRanking}
                            color={levelColorsList[leaderboard?.agentLevelRanking - 1]}
                          />
                        </Typography>
                      ) : (
                        <Typography variant="p5">--</Typography>
                      )}
                    </GridRow>
                  </GridColumn>
                </GridRow>
                {isAgentRangeMeterPresent ? (
                  <GridRow className={styles.agentLevelContainer}>
                    <GridColumn className={styles.leaderboardContainer}>
                      <AgentProgressMeter
                        levelConstructList={leaderboard.levelConstructList}
                        currentLevel={leaderboard.agentLevelRanking}
                        cashCollected={leaderboard.cashCollectedInLast30Days}
                        iconStyle={styles.agentLevelIcon}
                        fontStyle={styles.agentLevelText}
                        maxLevel={leaderboard.maxLevel}
                      />
                    </GridColumn>
                  </GridRow>
                ) : null}
              </GridContainer>
            </GridColumn>
          </GridRow>
        </GridContainer>
      </GridContainer>
    </>
  );
};

export default DashBoard;
